import { FormEvent, useEffect, useMemo, useState } from 'react';
import { BannersPanel } from './admin/BannersPanel';
import { BulkPanel } from './admin/BulkPanel';
import { CustomersPanel } from './admin/CustomersPanel';
import { FinancePanel } from './admin/FinancePanel';
import { GiftcardsPanel } from './admin/GiftcardsPanel';
import { NewsletterPanel } from './admin/NewsletterPanel';
import { OrdersPanel, OrderStatusPill } from './admin/OrdersPanel';
import { PromotionsPanel } from './admin/PromotionsPanel';
import { ReportsPanel } from './admin/ReportsPanel';
import { SalesPanel } from './admin/SalesPanel';
import { SettingsPanel } from './admin/SettingsPanel';
import { api } from './lib/api';
import { classNames, formatCLP, slugify } from './lib/format';
import { siteUrl, useDocumentMeta } from './lib/seo';
import { trackPageView, useSiteTracking } from './lib/tracking';
import { AccountPage } from './pages/AccountPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { staticPages, type StaticPageContent } from './pages/StaticPages';
import { TrackingPage } from './pages/TrackingPage';
import type {
  AdminFeatures,
  Banner,
  CartLine,
  DashboardSummary,
  Order,
  Product,
  ProductImage,
  ProductVariant,
  StoreSender,
  StoreSettings
} from './types';

const WISHLIST_KEY = 'runmore_wishlist';

const DEFAULT_FEATURES: AdminFeatures = {
  mercadoPagoConfigured: false,
  starkenConfigured: false,
  emailConfigured: false
};

const DEFAULT_SENDER: StoreSender = {
  name: 'Run More Chile',
  address: 'Av. Ejemplo 123',
  city: 'Santiago',
  region: 'Region Metropolitana',
  phone: '+56 9 0000 0000',
  email: 'contacto@runmorechile.cl'
};

const previewProducts: Product[] = [
  {
    id: 'preview-legging-aero',
    slug: 'legging-aero-preview',
    name: 'Legging Aero Run',
    subtitle: 'Preview de temporada',
    description: 'Producto de muestra para validar diseño. Reemplazar por catálogo real desde el admin.',
    status: 'published',
    price: 44990,
    compareAtPrice: 59990,
    featured: true,
    seoTitle: '',
    seoDescription: '',
    categories: ['Fitness', 'Leggings'],
    images: [{ id: 'img-1', url: '', alt: 'Legging Aero Run', sortOrder: 0 }],
    variants: [
      { id: 'v-1', sku: 'PREVIEW-S', color: 'Negro', size: 'S', stock: 8, status: 'active' },
      { id: 'v-2', sku: 'PREVIEW-M', color: 'Negro', size: 'M', stock: 12, status: 'active' }
    ]
  },
  {
    id: 'preview-top-pulse',
    slug: 'top-pulse-preview',
    name: 'Top Pulse Training',
    subtitle: 'Preview de temporada',
    description: 'Producto de muestra para validar cards, carrito y variantes.',
    status: 'published',
    price: 32990,
    compareAtPrice: null,
    featured: true,
    seoTitle: '',
    seoDescription: '',
    categories: ['Fitness', 'Tops'],
    images: [{ id: 'img-2', url: '', alt: 'Top Pulse Training', sortOrder: 0 }],
    variants: [
      { id: 'v-3', sku: 'PREVIEW-TOP-S', color: 'Blanco', size: 'S', stock: 6, status: 'active' },
      { id: 'v-4', sku: 'PREVIEW-TOP-M', color: 'Blanco', size: 'M', stock: 9, status: 'active' }
    ]
  },
  {
    id: 'preview-short-race',
    slug: 'short-race-preview',
    name: 'Short Race Flow',
    subtitle: 'Preview de temporada',
    description: 'Producto de muestra para la estética inicial.',
    status: 'published',
    price: 38990,
    compareAtPrice: 46990,
    featured: false,
    seoTitle: '',
    seoDescription: '',
    categories: ['Corrida', 'Shorts'],
    images: [{ id: 'img-3', url: '', alt: 'Short Race Flow', sortOrder: 0 }],
    variants: [
      { id: 'v-5', sku: 'PREVIEW-SHORT-S', color: 'Gris', size: 'S', stock: 5, status: 'active' },
      { id: 'v-6', sku: 'PREVIEW-SHORT-M', color: 'Gris', size: 'M', stock: 7, status: 'active' }
    ]
  }
];

const heroImages = [
  '/hero/runmore-hero-1.jpg',
  '/hero/runmore-hero-2.jpg',
  '/hero/runmore-hero-3.jpg'
];

const menuItems = [
  { label: 'Fitness', columns: [['Leggings', 'Tops', 'Shorts', 'Bikers'], ['Chaquetas', 'Corsarios', 'Bermudas', 'Accesorios']] },
  { label: 'Essencial', columns: [['Basicos', 'Tops', 'Leggings'], ['Novedades', 'Mas vendidos']] },
  { label: 'Comfy', columns: [['Blusas', 'Calzas', 'Joggers'], ['Chaquetas', 'Faldas', 'Vestidos']] },
  { label: 'Urban', columns: [['Poleras', 'Chaquetas', 'Pantalones'], ['Looks completos', 'Nuevos ingresos']] },
  { label: 'Kids', columns: [['Mini fitness', 'Tops', 'Leggings'], ['Accesorios']] },
  { label: 'Corrida', columns: [['Running tops', 'Shorts', 'Leggings'], ['Alta compresion', 'Reflectivos']] },
  { label: 'Praia', columns: [['Bikinis', 'Salidas de playa', 'Vestidos'], ['Trajes de bano', 'Accesorios']] },
  { label: 'Outlet', columns: [['Hasta 40% off', 'Ultimas tallas'], ['Sale fitness', 'Sale praia']] }
];

const emptyProduct: Partial<Product> = {
  name: '',
  slug: '',
  subtitle: '',
  description: '',
  status: 'draft',
  price: 0,
  compareAtPrice: null,
  featured: false,
  categories: [],
  images: [],
  variants: [{ id: '', sku: '', color: '', size: '', stock: 0, status: 'active' }],
  seoTitle: '',
  seoDescription: ''
};

function useRoute() {
  const [location, setLocation] = useState({ path: window.location.pathname, search: window.location.search });

  useEffect(() => {
    const onPopState = () => setLocation({ path: window.location.pathname, search: window.location.search });
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function navigate(nextHref: string) {
    const url = new URL(nextHref, window.location.origin);
    const fullPath = url.pathname + url.search;
    window.history.pushState({}, '', fullPath);
    setLocation({ path: url.pathname, search: url.search });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  return { path: location.path, search: location.search, params, navigate };
}

function useCatalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [usingPreview, setUsingPreview] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const remoteProducts = await api.products();
      if (remoteProducts.length) {
        setProducts(remoteProducts);
        setUsingPreview(false);
      } else {
        setProducts(previewProducts);
        setUsingPreview(true);
      }
    } catch {
      setProducts(previewProducts);
      setUsingPreview(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return { products, usingPreview, loading, reload: load };
}

function useBanners() {
  const [banners, setBanners] = useState<Banner[]>([]);
  useEffect(() => {
    api.banners().then(setBanners).catch(() => setBanners([]));
  }, []);
  return banners;
}

function App() {
  const { path, params, navigate } = useRoute();
  const catalog = useCatalog();
  const banners = useBanners();
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState<CartLine[]>([]);

  useSiteTracking();
  useEffect(() => {
    trackPageView(path);
  }, [path]);

  function addToCart(product: Product, variant?: ProductVariant) {
    setCart((current) => {
      const key = `${product.id}:${variant?.id || 'default'}`;
      const existing = current.find((line) => `${line.product.id}:${line.variant?.id || 'default'}` === key);
      if (existing) {
        return current.map((line) => (line === existing ? { ...line, quantity: line.quantity + 1 } : line));
      }
      return [...current, { product, variant, quantity: 1 }];
    });
    setCartOpen(true);
  }

  function updateQuantity(index: number, quantity: number) {
    setCart((current) => current.flatMap((line, lineIndex) => (lineIndex === index ? (quantity > 0 ? [{ ...line, quantity }] : []) : [line])));
  }

  if (path.startsWith('/admin')) {
    return <AdminApp navigate={navigate} />;
  }

  if (path === '/checkout') {
    return <CheckoutPage cart={cart} navigate={navigate} updateQuantity={updateQuantity} />;
  }

  if (path === '/seguimiento' || path === '/tracking') {
    return (
      <>
        <StoreHeader navigate={navigate} params={params} cartCount={cart.reduce((sum, line) => sum + line.quantity, 0)} openCart={() => setCartOpen(true)} />
        <TrackingPage navigate={navigate} />
        <Footer navigate={navigate} />
      </>
    );
  }

  if (path === '/mi-cuenta' || path === '/cuenta') {
    return (
      <>
        <StoreHeader navigate={navigate} params={params} cartCount={cart.reduce((sum, line) => sum + line.quantity, 0)} openCart={() => setCartOpen(true)} />
        <AccountPage navigate={navigate} />
        <Footer navigate={navigate} />
      </>
    );
  }

  const productSlug = path.startsWith('/producto/') ? decodeURIComponent(path.replace('/producto/', '')) : '';
  const selectedProduct = productSlug ? catalog.products.find((product) => product.slug === productSlug) : null;
  const staticPage = staticPages[path];

  return (
    <>
      <StoreHeader
        navigate={navigate}
        params={params}
        cartCount={cart.reduce((sum, line) => sum + line.quantity, 0)}
        openCart={() => setCartOpen(true)}
      />
      {selectedProduct ? (
        <ProductDetail product={selectedProduct} allProducts={catalog.products} navigate={navigate} addToCart={addToCart} />
      ) : path.startsWith('/checkout/') ? (
        <CheckoutStatus path={path} navigate={navigate} />
      ) : staticPage ? (
        <StaticPageView page={staticPage} path={path} navigate={navigate} />
      ) : (
        <Storefront catalog={catalog} banners={banners} navigate={navigate} params={params} addToCart={addToCart} />
      )}
      <CartDrawer
        open={cartOpen}
        lines={cart}
        close={() => setCartOpen(false)}
        updateQuantity={updateQuantity}
        goToCheckout={() => {
          setCartOpen(false);
          navigate('/checkout');
        }}
      />
      <Footer navigate={navigate} />
    </>
  );
}

function StaticPageView({ page, path, navigate }: { page: StaticPageContent; path: string; navigate: (path: string) => void }) {
  useDocumentMeta({
    title: `${page.title} · Run More Chile`,
    description: page.intro.slice(0, 160),
    canonical: siteUrl(path)
  });
  return (
    <main className="static-page">
      <button className="back-button" onClick={() => navigate('/')}>Volver a tienda</button>
      <header className="static-page-header">
        <p className="eyebrow">{page.eyebrow}</p>
        <h1>{page.title}</h1>
        <p className="static-page-intro">{page.intro}</p>
      </header>
      <div className="static-page-body">{page.body}</div>
    </main>
  );
}

function StoreHeader({ navigate, params, cartCount, openCart }: { navigate: (path: string) => void; params: URLSearchParams; cartCount: number; openCart: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState(params.get('q') || '');

  useEffect(() => {
    setSearchTerm(params.get('q') || '');
  }, [params]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function goToCategory(name: string) {
    navigate(`/?cat=${encodeURIComponent(name)}`);
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const term = searchTerm.trim();
    navigate(term ? `/?q=${encodeURIComponent(term)}` : '/');
  }

  return (
    <header className={classNames('store-header', scrolled && 'scrolled')}>
      <button className="brand-mark" onClick={() => navigate('/')}>
        <img src="/brand/runmore-logo.png" alt="Run More" />
      </button>
      <nav className="desktop-nav" aria-label="Categorias principales">
        {menuItems.map((item) => (
          <div className="nav-item" key={item.label}>
            <button onClick={() => goToCategory(item.label)}>{item.label}</button>
            <div className="mega-menu">
              <strong>{item.label}</strong>
              <div className="mega-columns">
                {item.columns.map((column, columnIndex) => (
                  <div key={`${item.label}-${columnIndex}`}>
                    {column.map((link) => (
                      <a
                        href={`/?cat=${encodeURIComponent(link)}`}
                        key={link}
                        onClick={(event) => {
                          event.preventDefault();
                          goToCategory(link);
                        }}
                      >
                        {link}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
              <span role="button" tabIndex={0} onClick={() => goToCategory(item.label)}>
                Ver todo {item.label}
              </span>
            </div>
          </div>
        ))}
      </nav>
      <div className="header-actions">
        <form className="search-pill" onSubmit={submitSearch}>
          <span>Buscar</span>
          <input
            aria-label="Buscar productos"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="¿Qué buscas?"
          />
        </form>
        <button className="icon-action" aria-label="Ingresar" onClick={() => navigate('/admin')}>Ingresar</button>
        <button className="cart-action" onClick={openCart}>Carro <strong>{cartCount}</strong></button>
      </div>
    </header>
  );
}

function BannerStrip({ banners, navigate }: { banners: Banner[]; navigate: (path: string) => void }) {
  if (banners.length === 0) return null;
  return (
    <section className="banners-strip">
      {banners.map((banner) => (
        <article
          key={banner.id}
          className={classNames('banner-tile', !banner.imageUrl && 'no-image')}
          style={banner.imageUrl ? { backgroundImage: `url(${banner.imageUrl})` } : undefined}
          onClick={() => banner.ctaHref && navigate(banner.ctaHref)}
          role={banner.ctaHref ? 'button' : undefined}
          tabIndex={banner.ctaHref ? 0 : undefined}
          onKeyDown={(e) => {
            if (banner.ctaHref && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              navigate(banner.ctaHref);
            }
          }}
        >
          <div className="banner-tile-overlay">
            {banner.eyebrow && <p className="eyebrow">{banner.eyebrow}</p>}
            <h2>{banner.title}</h2>
            {banner.body && <p className="banner-body">{banner.body}</p>}
            {banner.ctaLabel && banner.ctaHref && (
              <span className="banner-cta">{banner.ctaLabel} →</span>
            )}
          </div>
        </article>
      ))}
    </section>
  );
}

function Storefront({ catalog, banners, navigate, params, addToCart }: { catalog: ReturnType<typeof useCatalog>; banners: Banner[]; navigate: (path: string) => void; params: URLSearchParams; addToCart: (product: Product, variant?: ProductVariant) => void }) {
  const activeCat = params.get('cat') || '';
  const activeQ = params.get('q') || '';
  const filterActive = Boolean(activeCat || activeQ);

  // SEO meta per view (home / category / search)
  const titleBase = 'Run More Chile · Moda fitness premium';
  const seoTitle = activeCat
    ? `${activeCat} · Run More Chile`
    : activeQ
      ? `Resultados: ${activeQ} · Run More Chile`
      : titleBase;
  const seoDescription = activeCat
    ? `Productos de ${activeCat} en Run More Chile. Despacho a todo Chile, pago seguro con MercadoPago.`
    : activeQ
      ? `Resultados de búsqueda para "${activeQ}" en Run More Chile.`
      : 'Run More Chile - moda fitness premium. Calzas, tops, shorts y conjuntos para entrenar y vivir. Despacho a todo Chile.';
  const canonical = activeCat
    ? siteUrl(`/?cat=${encodeURIComponent(activeCat)}`)
    : activeQ
      ? siteUrl(`/?q=${encodeURIComponent(activeQ)}`)
      : siteUrl('/');

  const itemListLd = useMemo(() => ({
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    itemListElement: catalog.products.slice(0, 20).map((p, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: siteUrl(`/producto/${p.slug}`),
      name: p.name
    }))
  }), [catalog.products]);

  useDocumentMeta({
    title: seoTitle,
    description: seoDescription,
    canonical,
    ogImage: catalog.products[0]?.images[0]?.url,
    jsonLd: filterActive ? [itemListLd] : undefined
  });

  const filteredProducts = useMemo(() => {
    const lowerQ = activeQ.toLowerCase().trim();
    return catalog.products.filter((product) => {
      if (activeCat) {
        const hit = product.categories.some((category) => category.toLowerCase() === activeCat.toLowerCase());
        if (!hit) return false;
      }
      if (lowerQ) {
        const haystack = [product.name, product.subtitle, product.description, ...product.categories].join(' ').toLowerCase();
        if (!haystack.includes(lowerQ)) return false;
      }
      return true;
    });
  }, [catalog.products, activeCat, activeQ]);

  const categories = useMemo(() => [...new Set(catalog.products.flatMap((product) => product.categories))].slice(0, 6), [catalog.products]);

  function clearFilter(field: 'cat' | 'q') {
    const remaining = new URLSearchParams(params);
    remaining.delete(field);
    const query = remaining.toString();
    navigate(query ? `/?${query}` : '/');
  }

  return (
    <main>
      {catalog.usingPreview && (
        <div className="preview-strip">Vista previa sin catálogo real. Cuando carguemos la nueva temporada, estos productos desaparecen.</div>
      )}
      {!filterActive && (
        <>
          <section className="hero-section">
            <div className="hero-copy">
              <p className="eyebrow">Run More Chile</p>
              <h1>Moda fitness premium, directa y sin ruido.</h1>
              <p>Una experiencia ecommerce en blanco y negro, rápida, editorial y pensada para comprar fácil desde celular.</p>
              <div className="hero-actions">
                <button className="primary-button" onClick={() => document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' })}>Comprar ahora</button>
                <button className="ghost-button" onClick={() => navigate('/admin')}>Entrar al admin</button>
              </div>
            </div>
            <div className="hero-art" aria-label="Campaña Run More">
              <div className="hero-mosaic">
                {[0, 1, 2].map((tileIndex) => (
                  <div className={`hero-tile hero-tile-${tileIndex + 1}`} key={tileIndex}>
                    {heroImages.map((image, imageIndex) => (
                      <img
                        src={image}
                        alt=""
                        key={image}
                        style={{ animationDelay: `${imageIndex * 4 + tileIndex * 0.65}s` }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="hero-logo-card">
                <img src="/brand/runmore-logo.png" alt="" />
                <span>Chile</span>
              </div>
            </div>
          </section>

          <section className="signal-row" aria-label="Beneficios">
            <span>Moda fitness</span>
            <span>Despacho a todo Chile</span>
            <span>MercadoPago</span>
            <span>Cambios simples</span>
          </section>

          <BannerStrip banners={banners} navigate={navigate} />

          <section className="category-band">
            {(categories.length ? categories : ['Fitness', 'Comfy', 'Corrida']).map((category, index) => (
              <article
                className="category-card"
                key={category}
                onClick={() => navigate(`/?cat=${encodeURIComponent(category)}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/?cat=${encodeURIComponent(category)}`);
                  }
                }}
              >
                <span>0{index + 1}</span>
                <h2>{category}</h2>
                <p>Ver colección</p>
              </article>
            ))}
          </section>

          <VideoSection />
        </>
      )}

      <section className="catalog-section" id="catalogo">
        <div className="section-heading">
          <p className="eyebrow">{filterActive ? 'Resultados' : 'Shop the drop'}</p>
          <h2>{activeCat ? activeCat : activeQ ? `Búsqueda: ${activeQ}` : 'Productos destacados'}</h2>
          <span>
            {catalog.loading ? 'Cargando...' : `${filteredProducts.length} ${filteredProducts.length === 1 ? 'producto' : 'productos'}`}
          </span>
        </div>
        {filterActive && (
          <div className="filter-chips">
            {activeCat && (
              <button className="filter-chip" onClick={() => clearFilter('cat')} aria-label={`Quitar filtro ${activeCat}`}>
                {activeCat} <span aria-hidden="true">×</span>
              </button>
            )}
            {activeQ && (
              <button className="filter-chip" onClick={() => clearFilter('q')} aria-label={`Quitar búsqueda ${activeQ}`}>
                "{activeQ}" <span aria-hidden="true">×</span>
              </button>
            )}
            <button className="filter-chip ghost" onClick={() => navigate('/')}>Limpiar todo</button>
          </div>
        )}
        {filteredProducts.length === 0 && !catalog.loading ? (
          <div className="empty-state">
            <p>No encontramos productos que coincidan con ese filtro.</p>
            <button className="ghost-button" onClick={() => navigate('/')}>Ver todo el catálogo</button>
          </div>
        ) : (
          <div className="product-grid">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} navigate={navigate} addToCart={addToCart} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function VideoSection() {
  return (
    <section className="video-section">
      <div className="video-copy">
        <p className="eyebrow">Run More TV</p>
        <h2>La campaña también se siente en movimiento.</h2>
        <p>Este bloque replica el recurso editorial de Brasil: video central, marca fuerte y transición hacia productos.</p>
      </div>
      <div className="video-frame">
        <iframe
          title="Run More campaign video"
          src="https://www.youtube.com/embed/-ng_memtKPw?rel=0&loop=1&mute=1&modestbranding=1&playlist=-ng_memtKPw"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    </section>
  );
}

function readWishlist(): string[] {
  try {
    const raw = window.localStorage.getItem(WISHLIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeWishlist(ids: string[]) {
  window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
  window.dispatchEvent(new CustomEvent('runmore-wishlist'));
}

function useWishlist() {
  const [ids, setIds] = useState<string[]>(readWishlist());
  useEffect(() => {
    const sync = () => setIds(readWishlist());
    window.addEventListener('runmore-wishlist', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('runmore-wishlist', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  function toggle(id: string) {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    writeWishlist(next);
  }
  return { ids, toggle };
}

function ProductCard({ product, navigate, addToCart }: { product: Product; navigate: (path: string) => void; addToCart: (product: Product, variant?: ProductVariant) => void }) {
  const firstVariant = product.variants.find((variant) => variant.status === 'active');
  const totalStock = product.variants.reduce((sum, v) => sum + (v.status === 'active' ? v.stock : 0), 0);
  const isLow = totalStock > 0 && totalStock <= 5;
  const isOut = product.variants.length > 0 && totalStock === 0;
  const { ids, toggle } = useWishlist();
  const wishlisted = ids.includes(product.id);

  return (
    <article className="product-card">
      <button
        type="button"
        className={classNames('wishlist-heart', wishlisted && 'active')}
        onClick={(e) => { e.stopPropagation(); toggle(product.id); }}
        aria-label={wishlisted ? 'Quitar de favoritos' : 'Agregar a favoritos'}
        title={wishlisted ? 'Quitar de favoritos' : 'Agregar a favoritos'}
      >
        {wishlisted ? '♥' : '♡'}
      </button>
      <button className="product-image" onClick={() => navigate(`/producto/${product.slug}`)}>
        {product.images[0]?.url ? (
          <img src={product.images[0].url} alt={product.images[0].alt || product.name} loading="lazy" decoding="async" />
        ) : (
          <span>{product.name.split(' ').slice(0, 2).join(' ')}</span>
        )}
        {product.compareAtPrice ? <i>Sale</i> : null}
        {isLow && <span className="stock-flag">Pocas unidades</span>}
        {isOut && <span className="stock-flag out">Agotado</span>}
      </button>
      <div className="product-meta">
        <p>{product.categories[0] || 'Run More'}</p>
        <h3>{product.name}</h3>
        <div className="price-line">
          <strong>{formatCLP(product.price)}</strong>
          {product.compareAtPrice ? <s>{formatCLP(product.compareAtPrice)}</s> : null}
        </div>
      </div>
      <button className="quick-add" onClick={() => addToCart(product, firstVariant)} disabled={isOut}>
        {isOut ? 'Agotado' : 'Agregar'}
      </button>
    </article>
  );
}

const SIZE_GUIDE_ROWS: Array<{ size: string; bust: string; waist: string; hip: string }> = [
  { size: 'XS', bust: '78-82', waist: '60-64', hip: '86-90' },
  { size: 'S', bust: '83-87', waist: '65-69', hip: '91-95' },
  { size: 'M', bust: '88-92', waist: '70-74', hip: '96-100' },
  { size: 'L', bust: '93-97', waist: '75-79', hip: '101-105' },
  { size: 'XL', bust: '98-102', waist: '80-84', hip: '106-110' }
];

function ProductDetail({ product, allProducts, navigate, addToCart }: { product: Product; allProducts: Product[]; navigate: (path: string) => void; addToCart: (product: Product, variant?: ProductVariant) => void }) {
  const [variantId, setVariantId] = useState(product.variants[0]?.id || '');
  const [activeImage, setActiveImage] = useState(0);
  const [zooming, setZooming] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const variant = product.variants.find((entry) => entry.id === variantId);

  const totalStock = product.variants.reduce((sum, v) => sum + (v.status === 'active' ? v.stock : 0), 0);
  const description = product.seoTitle ? (product.seoDescription || product.description.slice(0, 155)) : product.description.slice(0, 155) || `${product.name} de Run More Chile`;
  const productTitle = product.seoTitle || `${product.name} · Run More Chile`;

  const productLd = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description,
    sku: product.variants[0]?.sku || product.id,
    image: product.images.map((i) => i.url).filter(Boolean),
    brand: { '@type': 'Brand', name: 'Run More Chile' },
    category: product.categories[0] || 'Fitness',
    offers: {
      '@type': 'Offer',
      priceCurrency: 'CLP',
      price: product.price,
      availability: totalStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      url: siteUrl(`/producto/${product.slug}`)
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: siteUrl('/') },
      ...(product.categories[0]
        ? [{ '@type': 'ListItem', position: 2, name: product.categories[0], item: siteUrl(`/?cat=${encodeURIComponent(product.categories[0])}`) }]
        : []),
      { '@type': 'ListItem', position: product.categories[0] ? 3 : 2, name: product.name, item: siteUrl(`/producto/${product.slug}`) }
    ]
  };

  useDocumentMeta({
    title: productTitle,
    description,
    canonical: siteUrl(`/producto/${product.slug}`),
    ogType: 'product',
    ogImage: product.images[0]?.url,
    jsonLd: [productLd, breadcrumbLd]
  });
  const isLow = totalStock > 0 && totalStock <= 5;
  const isOut = product.variants.length > 0 && totalStock === 0;
  const variantStock = variant?.stock ?? 0;

  const related = useMemo(() => {
    return allProducts
      .filter((p) => p.id !== product.id && p.status === 'published')
      .map((p) => {
        const sharedCats = p.categories.filter((c) => product.categories.includes(c)).length;
        return { p, score: sharedCats };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((entry) => entry.p);
  }, [allProducts, product]);

  return (
    <main className="product-detail">
      <button className="back-button" onClick={() => navigate('/')}>Volver a tienda</button>
      <div className="detail-gallery">
        <div
          className={classNames('detail-image', zooming && 'zooming')}
          onMouseEnter={() => setZooming(true)}
          onMouseLeave={() => setZooming(false)}
        >
          {product.images[activeImage]?.url ? (
            <img src={product.images[activeImage].url} alt={product.images[activeImage].alt || product.name} />
          ) : (
            <span>{product.name}</span>
          )}
          {isLow && !isOut && <span className="stock-flag">Pocas unidades</span>}
          {isOut && <span className="stock-flag out">Agotado</span>}
        </div>
        {product.images.length > 1 && (
          <div className="detail-thumbs">
            {product.images.map((image, index) => (
              <button
                key={image.id || image.url}
                type="button"
                className={classNames('detail-thumb', activeImage === index && 'active')}
                onClick={() => setActiveImage(index)}
                aria-label={`Imagen ${index + 1}`}
              >
                <img src={image.url} alt={image.alt || `${product.name} ${index + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
      <section className="detail-copy">
        <p className="eyebrow">{product.categories.join(' / ') || 'Run More'}</p>
        <h1>{product.name}</h1>
        {product.subtitle && <p className="detail-subtitle">{product.subtitle}</p>}
        <div className="price-line large">
          <strong>{formatCLP(variant?.priceOverride || product.price)}</strong>
          {product.compareAtPrice ? <s>{formatCLP(product.compareAtPrice)}</s> : null}
          {product.compareAtPrice && product.compareAtPrice > product.price && (
            <span className="discount-badge">-{Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}%</span>
          )}
        </div>
        <p className="detail-description">{product.description}</p>

        {product.variants.length > 0 && (
          <>
            <div className="variant-pills">
              <p className="field-label inline-label">
                Talla / color
                <button type="button" className="link-button size-guide-toggle" onClick={() => setShowSizeGuide(true)}>Guía de tallas</button>
              </p>
              <div className="variant-pill-row">
                {product.variants.map((entry) => {
                  const label = [entry.color, entry.size].filter(Boolean).join(' · ');
                  const disabled = entry.stock === 0 || entry.status !== 'active';
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={classNames('variant-pill', variantId === entry.id && 'active', disabled && 'disabled')}
                      onClick={() => !disabled && setVariantId(entry.id)}
                      disabled={disabled}
                      aria-pressed={variantId === entry.id}
                    >
                      {label}
                      {disabled && <small> · agotado</small>}
                    </button>
                  );
                })}
              </div>
              {variant && variant.stock > 0 && variant.stock <= 3 && (
                <p className="muted small stock-warning">Quedan solo {variant.stock} en {[variant.color, variant.size].filter(Boolean).join(' / ')}</p>
              )}
            </div>
          </>
        )}

        <button
          className="primary-button full"
          onClick={() => addToCart(product, variant)}
          disabled={isOut || (variant && variantStock === 0)}
        >
          {isOut ? 'Agotado' : variant && variantStock === 0 ? 'Sin stock en esta variante' : 'Agregar al carrito'}
        </button>

        <ul className="detail-perks">
          <li>📦 Despacho a todo Chile vía Starken</li>
          <li>🔒 Compra segura con MercadoPago</li>
          <li>↩️ 30 días para cambios</li>
        </ul>
      </section>

      {related.length > 0 && (
        <section className="related-products">
          <div className="section-heading">
            <p className="eyebrow">También te puede gustar</p>
            <h2>Productos relacionados</h2>
          </div>
          <div className="product-grid">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} navigate={navigate} addToCart={addToCart} />
            ))}
          </div>
        </section>
      )}

      {showSizeGuide && (
        <div className="modal-overlay" onClick={() => setShowSizeGuide(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>Guía de tallas</h2>
              <button type="button" className="modal-close" onClick={() => setShowSizeGuide(false)} aria-label="Cerrar">×</button>
            </div>
            <p className="muted">Medidas referenciales en cm. Si estás entre dos tallas, te recomendamos elegir la mayor.</p>
            <table className="size-guide-table">
              <thead>
                <tr><th>Talla</th><th>Busto</th><th>Cintura</th><th>Cadera</th></tr>
              </thead>
              <tbody>
                {SIZE_GUIDE_ROWS.map((row) => (
                  <tr key={row.size}>
                    <td><strong>{row.size}</strong></td>
                    <td>{row.bust}</td>
                    <td>{row.waist}</td>
                    <td>{row.hip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted small">¿Dudas? Escríbenos por WhatsApp y te asesoramos.</p>
          </div>
        </div>
      )}
    </main>
  );
}

function CartDrawer({
  open,
  lines,
  close,
  updateQuantity,
  goToCheckout
}: {
  open: boolean;
  lines: CartLine[];
  close: () => void;
  updateQuantity: (index: number, quantity: number) => void;
  goToCheckout: () => void;
}) {
  const subtotal = lines.reduce((sum, line) => sum + (line.variant?.priceOverride || line.product.price) * line.quantity, 0);

  return (
    <aside className={classNames('cart-drawer', open && 'open')} aria-hidden={!open}>
      <div className="drawer-header">
        <h2>Tu carro</h2>
        <button className="link-button" onClick={close}>Cerrar</button>
      </div>
      {lines.length === 0 ? (
        <div className="cart-empty">
          <p className="muted">Tu carro esta vacio.</p>
          <button className="ghost-button" onClick={close}>Seguir comprando</button>
        </div>
      ) : (
        <>
          <div className="cart-lines">
            {lines.map((line, index) => {
              const unit = line.variant?.priceOverride || line.product.price;
              return (
                <article className="cart-line" key={`${line.product.id}-${line.variant?.id || 'default'}`}>
                  <div className="cart-line-thumb">
                    {line.product.images[0]?.url ? (
                      <img src={line.product.images[0].url} alt={line.product.name} />
                    ) : (
                      <span>{line.product.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="cart-line-meta">
                    <strong>{line.product.name}</strong>
                    {(line.variant?.color || line.variant?.size) && (
                      <span className="cart-line-variant">{[line.variant?.color, line.variant?.size].filter(Boolean).join(' / ')}</span>
                    )}
                    <div className="quantity-control">
                      <button type="button" onClick={() => updateQuantity(index, line.quantity - 1)} aria-label="Restar">−</button>
                      <span>{line.quantity}</span>
                      <button type="button" onClick={() => updateQuantity(index, line.quantity + 1)} aria-label="Sumar">+</button>
                    </div>
                  </div>
                  <div className="cart-line-price">{formatCLP(unit * line.quantity)}</div>
                </article>
              );
            })}
          </div>
          <footer className="cart-footer">
            <div className="cart-total">
              <span>Subtotal</span>
              <strong>{formatCLP(subtotal)}</strong>
            </div>
            <p className="cart-note muted">Despacho y descuentos se calculan en el checkout.</p>
            <button type="button" className="primary-button full" onClick={goToCheckout}>
              Ir a pagar →
            </button>
            <button type="button" className="ghost-button full" onClick={close}>
              Seguir comprando
            </button>
          </footer>
        </>
      )}
    </aside>
  );
}

function CheckoutStatus({ path, navigate }: { path: string; navigate: (path: string) => void }) {
  const status = path.includes('exito') ? 'Pago recibido' : path.includes('error') ? 'Pago no completado' : 'Pago pendiente';
  return (
    <main className="status-page">
      <p className="eyebrow">Checkout</p>
      <h1>{status}</h1>
      <p>Estamos preparando la experiencia post-compra para dejarla tan clara como Shopify.</p>
      <button className="primary-button" onClick={() => navigate('/')}>Volver a tienda</button>
    </main>
  );
}

function AdminLogin({ navigate, onLoggedIn }: { navigate: (path: string) => void; onLoggedIn: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const result = await api.admin.login(email, password);
      onLoggedIn(result.token);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'No pudimos iniciar sesion');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="admin-login">
      <button className="back-button" onClick={() => navigate('/')}>Volver a tienda</button>
      <form onSubmit={submit} className="admin-login-form">
        <p className="eyebrow">Run More Admin</p>
        <h1>Entrar al panel</h1>
        <p className="admin-login-help">Usa el email y la contrasena que te entregamos. Si no las recuerdas, escribenos por WhatsApp.</p>
        <label className="login-field">
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="contacto@runmorechile.cl"
            required
          />
        </label>
        <label className="login-field">
          Contrasena
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="********"
            required
          />
        </label>
        {error && <p className="form-message">{error}</p>}
        <button className="primary-button full" disabled={submitting}>
          {submitting ? 'Entrando...' : 'Entrar al admin'}
        </button>
      </form>
    </main>
  );
}

function AdminApp({ navigate }: { navigate: (path: string) => void }) {
  useDocumentMeta({ title: 'Admin · Run More Chile', noindex: true });
  const [token, setToken] = useState(window.localStorage.getItem('runmore_admin_token') || '');
  const [authorized, setAuthorized] = useState(Boolean(token));
  const [activeTab, setActiveTab] = useState('inicio');
  const [advanced, setAdvanced] = useState(window.localStorage.getItem('runmore_admin_advanced') === 'true');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [features, setFeatures] = useState<AdminFeatures>(DEFAULT_FEATURES);
  const [storeSender, setStoreSender] = useState<StoreSender>(DEFAULT_SENDER);

  async function loadAdmin() {
    try {
      const [nextSummary, nextProducts, nextOrders] = await Promise.all([api.admin.summary(), api.admin.products(), api.admin.orders()]);
      setSummary(nextSummary);
      setProducts(nextProducts);
      setOrders(nextOrders);
      setFeatures(nextSummary.features || DEFAULT_FEATURES);
      setStoreSender(nextSummary.storeSender || DEFAULT_SENDER);
      setAuthorized(true);
    } catch {
      setAuthorized(false);
      setToken('');
      window.localStorage.removeItem('runmore_admin_token');
    }
  }

  // suppress unused warning while we wire it back in below
  void storeSender;

  useEffect(() => {
    if (token) loadAdmin();
  }, [token]);

  function handleLoggedIn(nextToken: string) {
    window.localStorage.setItem('runmore_admin_token', nextToken);
    setToken(nextToken);
  }

  async function logOut() {
    await api.admin.logout();
    window.localStorage.removeItem('runmore_admin_token');
    setToken('');
    setAuthorized(false);
  }

  function toggleAdvanced(nextValue: boolean) {
    setAdvanced(nextValue);
    window.localStorage.setItem('runmore_admin_advanced', String(nextValue));
  }

  if (!authorized) {
    return <AdminLogin navigate={navigate} onLoggedIn={handleLoggedIn} />;
  }

  return (
    <main className="admin-shell">
      <aside className="admin-sidebar">
        <button className="brand-mark admin-brand" onClick={() => navigate('/')}>
          <img src="/brand/runmore-logo.png" alt="Run More" />
          <i>Admin</i>
        </button>
        <nav className="admin-nav">
          {['inicio', 'productos', 'categorias', 'promociones', 'giftcards', 'pedidos', 'ventas', 'clientes', 'reportes', 'finanzas', 'banners', 'newsletter', 'configuracion'].map((tab) => (
            <button key={tab} className={classNames(activeTab === tab && 'active')} onClick={() => setActiveTab(tab)}>{tab}</button>
          ))}
        </nav>
        <button className="admin-logout" onClick={logOut}>Cerrar sesion</button>
      </aside>
      <section className="admin-main">
        <div className="admin-topbar">
          <div>
            <p className="eyebrow">Panel operativo</p>
            <h1>{activeTab}</h1>
          </div>
          <label className="mode-toggle">
            <input type="checkbox" checked={advanced} onChange={(event) => toggleAdvanced(event.target.checked)} />
            <span>{advanced ? 'Modo avanzado activo' : 'Modo simple'}</span>
          </label>
        </div>
        {activeTab === 'inicio' && <AdminHome summary={summary} advanced={advanced} navigate={navigate} />}
        {activeTab === 'productos' && <AdminProducts products={products} advanced={advanced} reload={loadAdmin} />}
        {activeTab === 'categorias' && <AdminCategories products={products} advanced={advanced} navigate={navigate} />}
        {activeTab === 'promociones' && <PromotionsPanel advanced={advanced} />}
        {activeTab === 'giftcards' && <GiftcardsPanel advanced={advanced} />}
        {activeTab === 'pedidos' && (
          <OrdersPanel orders={orders} features={features} storeSender={summary?.storeSender || DEFAULT_SENDER} reload={loadAdmin} />
        )}
        {activeTab === 'ventas' && <SalesPanel products={products} reload={loadAdmin} />}
        {activeTab === 'banners' && <BannersPanel advanced={advanced} />}
        {activeTab === 'clientes' && <CustomersPanel advanced={advanced} />}
        {activeTab === 'reportes' && <ReportsPanel />}
        {activeTab === 'finanzas' && <FinancePanel advanced={advanced} />}
        {activeTab === 'newsletter' && <NewsletterPanel />}
        {activeTab === 'configuracion' && <SettingsPanel advanced={advanced} />}
      </section>
    </main>
  );
}

function AdminHome({ summary, advanced, navigate }: { summary: DashboardSummary | null; advanced: boolean; navigate: (path: string) => void }) {
  const cards = [
    { label: 'Productos publicados', value: summary?.publishedProducts || 0 },
    { label: 'Borradores', value: summary?.draftProducts || 0 },
    { label: 'Pedidos activos', value: summary?.pendingOrders || 0 },
    { label: 'Stock bajo', value: summary?.lowStockVariants || 0 }
  ];
  const features = summary?.features;
  const integrationAlerts: Array<{ key: string; text: string }> = [];
  if (features) {
    if (!features.mercadoPagoConfigured) {
      integrationAlerts.push({ key: 'mp', text: 'MercadoPago aun no esta conectado: los checkouts caen en modo prueba.' });
    }
    if (!features.emailConfigured) {
      integrationAlerts.push({ key: 'email', text: 'Email transaccional desactivado: no se envia notificacion al cliente cuando entra un pedido.' });
    }
    if (!features.starkenConfigured) {
      integrationAlerts.push({ key: 'starken', text: 'Starken no esta enchufado: las etiquetas se imprimen como packing slip simple, sin guia oficial.' });
    }
  }

  const sales = summary?.salesByDay || [];
  const topProducts = summary?.topProducts || [];
  const recentOrders = summary?.recentOrders || [];
  const last30 = summary?.last30Days;

  return (
    <div className="dashboard-stack">
      <div className="admin-grid">
        {cards.map((card) => (
          <article className="metric-card" key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </article>
        ))}
      </div>

      <article className="admin-card sales-card">
        <div className="admin-card-head">
          <div>
            <h2>Ventas ultimos 14 dias</h2>
            <p className="muted">
              {last30 ? `${formatCLP(last30.revenue)} en 30 dias (${last30.orders} pedidos validos).` : 'Cuando entren pedidos veras la curva acumulada aca.'}
            </p>
          </div>
        </div>
        <SalesChart points={sales} />
      </article>

      <div className="admin-grid two-col">
        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Pedidos recientes</h2>
            <button type="button" className="ghost-button compact" onClick={() => navigate('/admin')}>Ver todos</button>
          </div>
          {recentOrders.length === 0 ? (
            <div className="empty-state subtle">
              <p>Aun no hay pedidos registrados.</p>
              <p className="muted">Cuando entre el primero, aparece aca con su estado.</p>
            </div>
          ) : (
            <ul className="recent-orders">
              {recentOrders.map((order) => (
                <li key={order.id}>
                  <div>
                    <strong>#{order.orderNumber}</strong>
                    <span>{order.customerName}</span>
                  </div>
                  <div className="recent-meta">
                    <OrderStatusPill status={order.status} />
                    <span>{formatCLP(order.total)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="admin-card">
          <div className="admin-card-head">
            <h2>Top productos (30 dias)</h2>
          </div>
          {topProducts.length === 0 ? (
            <div className="empty-state subtle">
              <p>Aun no hay ventas para rankear.</p>
              <p className="muted">Despues de la primera semana operativa veras tus best sellers.</p>
            </div>
          ) : (
            <ul className="top-products">
              {topProducts.map((product, index) => (
                <li key={product.name}>
                  <span className="top-rank">{String(index + 1).padStart(2, '0')}</span>
                  <div className="top-meta">
                    <strong>{product.name}</strong>
                    <small>{product.quantity} {product.quantity === 1 ? 'unidad vendida' : 'unidades vendidas'}</small>
                  </div>
                  <span className="top-revenue">{formatCLP(product.revenue)}</span>
                </li>
              ))}
            </ul>
          )}
        </article>
      </div>

      <article className="admin-card wide">
        <h2>Flujo recomendado</h2>
        <ol className="flow-list">
          <li>Carga las fotos y datos del producto en <strong>Productos → Nuevo</strong>.</li>
          <li>Define tallas, colores y stock en la pestana <strong>Tallas y stock</strong>.</li>
          <li>Cambia el estado a <strong>Publicado</strong> cuando este listo para vender.</li>
          <li>Revisa <strong>Pedidos</strong> cada dia para preparar despachos.</li>
        </ol>
        {advanced && <p className="advanced-note">Avanzado: aca sumaremos importacion masiva, auditoria y export CSV.</p>}
      </article>

      {integrationAlerts.length > 0 && (
        <article className="admin-card wide alerts-card">
          <h2>Recordatorios de configuracion</h2>
          <ul className="alerts-list">
            {integrationAlerts.map((alert) => (
              <li key={alert.key}>{alert.text}</li>
            ))}
          </ul>
        </article>
      )}
    </div>
  );
}

function SalesChart({ points }: { points: { date: string; count: number; total: number } [] }) {
  const width = 720;
  const height = 200;
  const padding = { top: 16, right: 16, bottom: 28, left: 36 };
  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;
  const maxValue = Math.max(1, ...points.map((point) => point.total));
  const barGap = 6;
  const barWidth = points.length > 0 ? Math.max(4, (usableWidth - barGap * (points.length - 1)) / points.length) : 0;

  function fmtDay(iso: string) {
    const day = new Date(iso + 'T12:00:00Z');
    return day.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  }

  return (
    <div className="sales-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Ventas por dia">
        <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} stroke="#d8d8d2" />
        {[0.25, 0.5, 0.75, 1].map((ratio) => (
          <line
            key={ratio}
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + usableHeight * (1 - ratio)}
            y2={padding.top + usableHeight * (1 - ratio)}
            stroke="rgba(5, 5, 5, 0.05)"
          />
        ))}
        {points.map((point, index) => {
          const barHeight = (point.total / maxValue) * usableHeight;
          const x = padding.left + index * (barWidth + barGap);
          const y = padding.top + usableHeight - barHeight;
          return (
            <g key={point.date}>
              <rect x={x} y={y} width={barWidth} height={Math.max(2, barHeight)} fill="#050505" rx={3}>
                <title>{`${point.date}: ${point.count} pedidos · ${new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(point.total)}`}</title>
              </rect>
              {index % 2 === 0 && (
                <text x={x + barWidth / 2} y={height - 8} textAnchor="middle" fontSize="10" fill="#77756d">
                  {fmtDay(point.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

type ProductSection = 'general' | 'descripcion' | 'imagenes' | 'variantes' | 'categorias' | 'seo';
type AdminMessage = { tone: 'ok' | 'error' | 'info'; text: string } | null;

function AdminProducts({ products, advanced, reload }: { products: Product[]; advanced: boolean; reload: () => Promise<void> }) {
  const [editing, setEditing] = useState<Partial<Product>>(emptyProduct);
  const [activeSection, setActiveSection] = useState<ProductSection>('general');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [message, setMessage] = useState<AdminMessage>(null);

  function pickProduct(product: Partial<Product>) {
    setEditing(product);
    setActiveSection('general');
    setMessage(null);
  }

  async function saveProduct(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api.admin.saveProduct(editing);
      setEditing(saved);
      setMessage({ tone: 'ok', text: '✓ Producto guardado correctamente.' });
      await reload();
    } catch (saveError) {
      setMessage({ tone: 'error', text: saveError instanceof Error ? saveError.message : 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  }

  async function archiveCurrent() {
    if (!editing.id) return;
    const confirmed = window.confirm(`¿Archivar el producto "${editing.name}"? Dejara de aparecer en la tienda. Podemos recuperarlo despues si hace falta.`);
    if (!confirmed) return;
    setArchiving(true);
    setMessage(null);
    try {
      await api.admin.archiveProduct(editing.id);
      setMessage({ tone: 'info', text: 'Producto archivado.' });
      pickProduct(emptyProduct);
      await reload();
    } catch (archiveError) {
      setMessage({ tone: 'error', text: archiveError instanceof Error ? archiveError.message : 'No se pudo archivar' });
    } finally {
      setArchiving(false);
    }
  }

  const allSections: Array<{ id: ProductSection; label: string; advancedOnly?: boolean }> = [
    { id: 'general', label: 'General' },
    { id: 'descripcion', label: 'Descripcion' },
    { id: 'imagenes', label: 'Imagenes' },
    { id: 'variantes', label: 'Tallas y stock' },
    { id: 'categorias', label: 'Categorias' },
    { id: 'seo', label: 'SEO', advancedOnly: true }
  ];
  const sections = allSections.filter((section) => !section.advancedOnly || advanced);
  const currentSection = sections.find((section) => section.id === activeSection)?.id || 'general';

  return (
    <div className="dashboard-stack">
    {advanced && <BulkPanel products={products} reload={reload} />}
    <div className="admin-two-column">
      <section className="admin-card product-list-panel">
        <div className="admin-card-head">
          <h2>Productos</h2>
          <button className="ghost-button compact" onClick={() => pickProduct(emptyProduct)}>+ Nuevo</button>
        </div>
        {products.length === 0 ? (
          <div className="empty-state subtle">
            <p>Todavia no hay productos.</p>
            <p className="muted">Crea el primero con el boton "Nuevo" arriba.</p>
          </div>
        ) : null}
        {products.map((product) => (
          <button
            type="button"
            className={classNames('product-row', editing.id === product.id && 'active')}
            key={product.id}
            onClick={() => pickProduct(product)}
          >
            <div className="product-row-thumb" aria-hidden="true">
              {product.images[0]?.url ? <img src={product.images[0].url} alt="" /> : <span>{product.name.slice(0, 2).toUpperCase()}</span>}
            </div>
            <div className="product-row-meta">
              <strong>{product.name}</strong>
              <small>{formatCLP(product.price)}</small>
            </div>
            <StatusPill status={product.status} compact />
          </button>
        ))}
      </section>

      <form className="admin-card product-form" onSubmit={saveProduct}>
        <div className="admin-card-head">
          <div>
            <h2>{editing.id ? 'Editar producto' : 'Nuevo producto'}</h2>
            {editing.id && <small className="muted">{editing.slug}</small>}
          </div>
          <div className="header-actions-row">
            {editing.id && (
              <button type="button" className="ghost-button compact danger" onClick={archiveCurrent} disabled={archiving}>
                {archiving ? 'Archivando...' : 'Archivar'}
              </button>
            )}
            <button className="primary-button compact" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>

        <nav className="product-tabs" aria-label="Secciones del producto">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={classNames('product-tab', currentSection === section.id && 'active')}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>

        <div className="product-section">
          {currentSection === 'general' && (
            <div className="form-grid">
              <label className="span-2">
                Nombre del producto
                <input
                  value={editing.name || ''}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value, slug: editing.slug || slugify(event.target.value) })}
                  placeholder="Ej: Legging Aero Run"
                  required
                />
              </label>
              <label>
                Estado
                <StatusPill
                  status={(editing.status as Product['status']) || 'draft'}
                  onChange={(status) => setEditing({ ...editing, status })}
                />
              </label>
              <label>
                Precio (CLP)
                <input
                  type="number"
                  min={0}
                  value={editing.price || 0}
                  onChange={(event) => setEditing({ ...editing, price: Number(event.target.value) })}
                  placeholder="34990"
                />
              </label>
              {advanced && (
                <label>
                  Precio antes (opcional)
                  <input
                    type="number"
                    min={0}
                    value={editing.compareAtPrice || 0}
                    onChange={(event) => setEditing({ ...editing, compareAtPrice: Number(event.target.value) || null })}
                    placeholder="Para mostrar tachado"
                  />
                </label>
              )}
              {advanced && (
                <label>
                  Costo (CLP) <em style={{ color: 'var(--steel)', fontSize: 11, fontStyle: 'normal' }}>solo admin</em>
                  <input
                    type="number"
                    min={0}
                    value={editing.costPrice || 0}
                    onChange={(event) => setEditing({ ...editing, costPrice: Number(event.target.value) })}
                    placeholder="Costo unitario para márgenes"
                  />
                </label>
              )}
              {advanced && (
                <label>
                  Slug en la URL
                  <input
                    value={editing.slug || ''}
                    onChange={(event) => setEditing({ ...editing, slug: slugify(event.target.value) })}
                    placeholder="legging-aero-run"
                  />
                </label>
              )}
              {advanced && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(editing.featured)}
                    onChange={(event) => setEditing({ ...editing, featured: event.target.checked })}
                  />
                  Mostrar como destacado en la home
                </label>
              )}
            </div>
          )}

          {currentSection === 'descripcion' && (
            <>
              <label>
                Subtitulo corto
                <input
                  value={editing.subtitle || ''}
                  onChange={(event) => setEditing({ ...editing, subtitle: event.target.value })}
                  placeholder="Ej: Coleccion verano 2026"
                />
              </label>
              <label>
                Descripcion
                <textarea
                  value={editing.description || ''}
                  onChange={(event) => setEditing({ ...editing, description: event.target.value })}
                  rows={10}
                  placeholder="Material, calce, recomendaciones de uso..."
                />
              </label>
            </>
          )}

          {currentSection === 'imagenes' && (
            <ProductImageGallery
              productName={editing.name || ''}
              images={editing.images || []}
              onChange={(images) => setEditing({ ...editing, images })}
              onMessage={setMessage}
            />
          )}

          {currentSection === 'variantes' && (
            <VariantEditor product={editing} setProduct={setEditing} advanced={advanced} />
          )}

          {currentSection === 'categorias' && (
            <CategoryEditor
              categories={editing.categories || []}
              onChange={(categories) => setEditing({ ...editing, categories })}
              suggestions={[...new Set(products.flatMap((product) => product.categories))].sort()}
            />
          )}

          {currentSection === 'seo' && advanced && (
            <div className="form-grid">
              <label className="span-2">
                Titulo SEO
                <input
                  value={editing.seoTitle || ''}
                  onChange={(event) => setEditing({ ...editing, seoTitle: event.target.value })}
                  placeholder={editing.name || 'Titulo para Google'}
                  maxLength={60}
                />
              </label>
              <label className="span-2">
                Descripcion SEO
                <textarea
                  rows={3}
                  value={editing.seoDescription || ''}
                  onChange={(event) => setEditing({ ...editing, seoDescription: event.target.value })}
                  placeholder="Resumen breve para resultados de Google (max 160 caracteres)"
                  maxLength={160}
                />
              </label>
            </div>
          )}
        </div>

        {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
      </form>
    </div>
    </div>
  );
}

function StatusPill({
  status,
  onChange,
  compact = false
}: {
  status: Product['status'];
  onChange?: (status: Product['status']) => void;
  compact?: boolean;
}) {
  const label = status === 'published' ? 'Publicado' : status === 'archived' ? 'Archivado' : 'Borrador';
  if (!onChange) {
    return <span className={classNames('status-pill', `pill-${status}`, compact && 'pill-compact')}>{label}</span>;
  }
  return (
    <div className="status-pill-toggle" role="radiogroup" aria-label="Estado del producto">
      <button
        type="button"
        role="radio"
        aria-checked={status === 'draft'}
        className={classNames('status-pill', 'pill-draft', status === 'draft' && 'active')}
        onClick={() => onChange('draft')}
      >
        Borrador
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={status === 'published'}
        className={classNames('status-pill', 'pill-published', status === 'published' && 'active')}
        onClick={() => onChange('published')}
      >
        Publicado
      </button>
    </div>
  );
}

function ProductImageGallery({
  productName,
  images,
  onChange,
  onMessage
}: {
  productName: string;
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
  onMessage: (message: AdminMessage) => void;
}) {
  const [uploading, setUploading] = useState(false);

  async function uploadFiles(fileList?: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    onMessage({ tone: 'info', text: 'Subiendo imagenes...' });
    try {
      const uploaded: ProductImage[] = [];
      for (const file of Array.from(fileList)) {
        const result = await api.admin.uploadAsset(file);
        uploaded.push({
          id: crypto.randomUUID(),
          url: result.url,
          alt: productName,
          sortOrder: images.length + uploaded.length
        });
      }
      onChange([...images, ...uploaded]);
      onMessage({ tone: 'ok', text: `✓ ${uploaded.length} imagen${uploaded.length === 1 ? '' : 'es'} cargadas.` });
    } catch (error) {
      onMessage({ tone: 'error', text: error instanceof Error ? error.message : 'No se pudo subir la imagen' });
    } finally {
      setUploading(false);
    }
  }

  function remove(index: number) {
    if (!window.confirm('¿Eliminar esta imagen del producto?')) return;
    onChange(images.filter((_, idx) => idx !== index).map((image, idx) => ({ ...image, sortOrder: idx })));
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= images.length) return;
    const next = [...images];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.map((image, idx) => ({ ...image, sortOrder: idx })));
  }

  return (
    <div className="image-gallery">
      <div className="image-gallery-head">
        <div>
          <h3>Imagenes del producto</h3>
          <p className="muted">La primera imagen es la portada en la tienda. Sugerencia: foto cuadrada o 4:5 en buena resolucion.</p>
        </div>
        <label className={classNames('upload-button', uploading && 'is-busy')}>
          {uploading ? 'Subiendo...' : '+ Agregar imagen'}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => uploadFiles(event.target.files)}
            disabled={uploading}
          />
        </label>
      </div>
      {images.length === 0 ? (
        <div className="empty-state subtle">
          <p>Aun no has subido imagenes.</p>
          <p className="muted">Sube al menos una para que el producto se vea en la tienda.</p>
        </div>
      ) : (
        <div className="image-gallery-grid">
          {images.map((image, index) => (
            <article className={classNames('image-card', index === 0 && 'is-cover')} key={image.id || image.url}>
              <div className="image-card-thumb">
                <img src={image.url} alt={image.alt || productName} />
                {index === 0 && <span className="image-card-badge">Portada</span>}
              </div>
              <div className="image-card-actions">
                <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Subir orden">↑</button>
                <button type="button" onClick={() => move(index, 1)} disabled={index === images.length - 1} aria-label="Bajar orden">↓</button>
                <button type="button" className="danger" onClick={() => remove(index)} aria-label="Eliminar imagen">Eliminar</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryEditor({
  categories,
  onChange,
  suggestions
}: {
  categories: string[];
  onChange: (categories: string[]) => void;
  suggestions: string[];
}) {
  const [draft, setDraft] = useState('');

  function addCategory(name: string) {
    const clean = name.trim();
    if (!clean) return;
    if (categories.some((existing) => existing.toLowerCase() === clean.toLowerCase())) return;
    onChange([...categories, clean]);
    setDraft('');
  }

  function removeCategory(name: string) {
    onChange(categories.filter((category) => category !== name));
  }

  const availableSuggestions = suggestions.filter(
    (suggestion) => !categories.some((existing) => existing.toLowerCase() === suggestion.toLowerCase())
  );

  return (
    <div className="category-editor">
      <p className="muted">Las categorias agrupan el producto en el menu y los filtros de la tienda. Ej: Fitness, Leggings.</p>
      <div className="category-input-row">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addCategory(draft);
            }
          }}
          placeholder="Escribe una categoria y presiona Enter"
        />
        <button type="button" className="ghost-button compact" onClick={() => addCategory(draft)}>Agregar</button>
      </div>
      <div className="category-tags">
        {categories.length === 0 ? (
          <span className="muted">Sin categorias asignadas todavia.</span>
        ) : (
          categories.map((category) => (
            <button type="button" className="category-tag" key={category} onClick={() => removeCategory(category)}>
              {category} <span aria-hidden="true">×</span>
            </button>
          ))
        )}
      </div>
      {availableSuggestions.length > 0 && (
        <div className="category-suggestions">
          <p className="muted">Sugerencias desde otros productos:</p>
          <div className="category-tags">
            {availableSuggestions.slice(0, 12).map((suggestion) => (
              <button type="button" className="category-tag suggestion" key={suggestion} onClick={() => addCategory(suggestion)}>
                + {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminCategories({ products, advanced, navigate }: { products: Product[]; advanced: boolean; navigate: (path: string) => void }) {
  const counts = products.reduce<Record<string, number>>((acc, product) => {
    for (const category of product.categories) {
      acc[category] = (acc[category] || 0) + 1;
    }
    return acc;
  }, {});
  const categories = Object.keys(counts).sort();
  const fallback = ['Fitness', 'Essencial', 'Comfy', 'Urban', 'Kids', 'Corrida', 'Praia', 'Outlet'];

  return (
    <section className="admin-card">
      <div className="admin-card-head">
        <div>
          <h2>Categorias</h2>
          <p className="muted">Las categorias salen automaticamente desde los productos. Para agregar una nueva, asignala dentro de un producto en la pestana <strong>Categorias</strong>.</p>
        </div>
      </div>
      {categories.length === 0 ? (
        <div className="empty-state subtle">
          <p>Aun no hay categorias asignadas a productos.</p>
          <p className="muted">Sugerencias para empezar: {fallback.join(', ')}.</p>
        </div>
      ) : (
        <div className="category-admin-grid">
          {categories.map((category, index) => (
            <article className="category-admin-card" key={category}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <strong>{category}</strong>
              <small>{counts[category]} {counts[category] === 1 ? 'producto' : 'productos'}</small>
              <button
                type="button"
                className="ghost-button compact"
                onClick={() => navigate(`/?cat=${encodeURIComponent(category)}`)}
              >
                Ver en la tienda →
              </button>
            </article>
          ))}
        </div>
      )}
      {advanced && (
        <div className="advanced-panel">
          <h3>Avanzado categorias</h3>
          <p>Aca sumaremos orden del menu, subcategorias, imagen de coleccion, SEO, visibilidad y reglas para mostrar promociones por categoria.</p>
        </div>
      )}
    </section>
  );
}

function VariantEditor({ product, setProduct, advanced }: { product: Partial<Product>; setProduct: (product: Partial<Product>) => void; advanced: boolean }) {
  const variants = product.variants?.length ? product.variants : [{ id: '', sku: '', color: '', size: '', stock: 0, status: 'active' as const }];

  function updateVariant(index: number, patch: Partial<ProductVariant>) {
    setProduct({ ...product, variants: variants.map((variant, variantIndex) => (variantIndex === index ? { ...variant, ...patch } : variant)) });
  }

  function addVariant() {
    setProduct({ ...product, variants: [...variants, { id: '', sku: '', color: '', size: '', stock: 0, status: 'active' }] });
  }

  function removeVariant(index: number) {
    const variant = variants[index];
    const label = [variant.color, variant.size].filter(Boolean).join(' / ') || 'esta variante';
    if (!window.confirm(`¿Eliminar "${label}"?`)) return;
    const next = variants.filter((_, variantIndex) => variantIndex !== index);
    setProduct({
      ...product,
      variants: next.length ? next : [{ id: '', sku: '', color: '', size: '', stock: 0, status: 'active' }]
    });
  }

  return (
    <section className="variant-editor">
      <div className="admin-card-head inline">
        <div>
          <h3>Tallas, colores y stock</h3>
          <p className="muted">Cada combinacion talla + color es una variante con su stock propio.</p>
        </div>
        <button type="button" className="ghost-button compact" onClick={addVariant}>+ Agregar variante</button>
      </div>
      <div className="variant-table">
        <div className="variant-row variant-head">
          <span>Color</span>
          <span>Talla</span>
          <span>Stock</span>
          {advanced && <span>SKU</span>}
          <span aria-hidden="true"></span>
        </div>
        {variants.map((variant, index) => (
          <div className="variant-row" key={`${variant.id}-${index}`}>
            <input placeholder="Negro" value={variant.color} onChange={(event) => updateVariant(index, { color: event.target.value })} />
            <input placeholder="M" value={variant.size} onChange={(event) => updateVariant(index, { size: event.target.value })} />
            <input
              placeholder="0"
              type="number"
              min={0}
              value={variant.stock}
              onChange={(event) => updateVariant(index, { stock: Number(event.target.value) })}
            />
            {advanced && (
              <input placeholder="SKU" value={variant.sku} onChange={(event) => updateVariant(index, { sku: event.target.value })} />
            )}
            <button
              type="button"
              className="variant-delete"
              onClick={() => removeVariant(index)}
              aria-label="Eliminar variante"
              title="Eliminar variante"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'busy' | 'ok' | 'err'>('idle');
  const [message, setMessage] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setStatus('busy');
    try {
      await api.newsletter(email);
      setStatus('ok');
      setMessage('¡Listo! Recibirás novedades pronto.');
      setEmail('');
    } catch (err) {
      setStatus('err');
      setMessage(err instanceof Error ? err.message : 'No se pudo suscribir');
    }
  }

  return (
    <form className="newsletter-signup" onSubmit={submit}>
      <p className="eyebrow">Newsletter</p>
      <strong>Recibe novedades, lanzamientos y descuentos exclusivos.</strong>
      <div className="newsletter-row">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.cl" />
        <button className="primary-button compact" disabled={status === 'busy'}>
          {status === 'busy' ? 'Enviando...' : 'Suscribirme'}
        </button>
      </div>
      {message && <small className={status === 'ok' ? 'newsletter-ok' : 'newsletter-err'}>{message}</small>}
    </form>
  );
}

function Footer({ navigate }: { navigate: (path: string) => void }) {
  const columns: Array<{ title: string; links: Array<{ label: string; path: string }> }> = [
    {
      title: 'Area cliente',
      links: [
        { label: 'Mi cuenta', path: '/mi-cuenta' },
        { label: 'Seguir mi pedido', path: '/seguimiento' },
        { label: 'Contacto', path: '/contacto' }
      ]
    },
    {
      title: 'Acceso restringido',
      links: [
        { label: 'Coleccion', path: '/' },
        { label: 'Catalogo mayorista', path: '/contacto' }
      ]
    },
    {
      title: 'Ayuda y soporte',
      links: [
        { label: 'Preguntas frecuentes', path: '/faq' },
        { label: 'Cuidados de la prenda', path: '/faq' },
        { label: 'Cambios y devoluciones', path: '/cambios' }
      ]
    },
    {
      title: 'Politicas y terminos',
      links: [
        { label: 'Politica de privacidad', path: '/faq' },
        { label: 'Terminos de uso', path: '/faq' },
        { label: 'Despacho gratis', path: '/faq' }
      ]
    },
    {
      title: 'Institucional',
      links: [
        { label: 'Quienes somos', path: '/quienes-somos' },
        { label: 'Sustentabilidad', path: '/quienes-somos' },
        { label: 'Correndo Juntas', path: '/quienes-somos' },
        { label: 'Trabaja con nosotros', path: '/contacto' }
      ]
    }
  ];

  return (
    <footer className="footer">
      <NewsletterSignup />
      <div className="footer-grid">
        {columns.map((column) => (
          <div className="footer-column" key={column.title}>
            <h3>{column.title}</h3>
            {column.links.map((link) => (
              <a
                href={link.path}
                key={link.label}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(link.path);
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
        ))}
        <div className="footer-column footer-contact">
          <h3>Atencion</h3>
          <a href="https://wa.me/56900000000">WhatsApp Run More Chile</a>
          <a href="mailto:contacto@runmorechile.cl">contacto@runmorechile.cl</a>
          <p>Lunes a viernes: 09:00 a 18:00</p>
        </div>
      </div>
      <div className="footer-bottom">
        <img src="/brand/runmore-logo.png" alt="Run More Chile" />
        <div>
          <span>Run More Chile</span>
          <span>|</span>
          <span>Ecommerce oficial Chile</span>
          <span>|</span>
          <span>MercadoPago + despacho Starken</span>
        </div>
      </div>
    </footer>
  );
}

export default App;
